// frontend/src/components/layout/AppLayout.jsx
// Updated application layout using AuthContext for enterprise-grade state management

import React from 'react';
import { useTranslation } from 'react-i18next';
import TopMenu from './TopMenu';
import Sidebar from './Sidebar';
import LocaleGate from '../common/LocaleGate';
import { ReorderLevelsProvider } from '../reorderLevels/ReorderLevelsProvider';
import StoreConnectionBanner from '../common/StoreConnectionBanner';
import PlanChangeApprovalBanner from '../billing/PlanChangeApprovalBanner';
import TabStrip from '../../tabs/TabStrip';
import './AppLayout.css';
import { useAuth } from '../../contexts/AuthContext';

export default function AppLayout({ content }) {
  const { t } = useTranslation();
  const { userContext, user, organization, session, isLoading } = useAuth();

  // Loading state, and only that. A userContext that arrived without a user
  // inside it is a broken payload, not a slower one — waiting on it here would
  // spin forever. TopMenu/UserDropdown read identity unconditionally and
  // utils/avatar raises, which surfaces in the ErrorBoundary (App.jsx).
  if (isLoading || !userContext) {
    return (
      <div className="app-wrapper">
        <div className="app-loading">
          <div className="spinner spinner-lg"></div>
          <p>{t('common.loadingApplication')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-wrapper">
      <Sidebar />
      <TopMenu />

      {/* Around the pages so any of them can open the reorder-levels dialog;
          the provider renders that dialog and its welcome popup itself. */}
      <ReorderLevelsProvider>
        <main className="main-content">
          <TabStrip />
          <div className="main-content-scroll">
            <StoreConnectionBanner />
            <PlanChangeApprovalBanner />
            {content}
          </div>
        </main>
      </ReorderLevelsProvider>

      {/* Mounted at the layout root so it fires for any authenticated
          page. The gate self-suppresses when User.{language, region_code,
          timezone} are all set — see LocaleGate for the predicate. */}
      <LocaleGate />

      {import.meta.env.DEV && (
        <div className="dev-info">
          {t('session.label')}: {session?.sessionActive ? 'Active' : 'Inactive'}<br />
          {t('session.organization')}: {organization?.slug}<br />
          {t('session.user')}: {user?.email}
        </div>
      )}
    </div>
  );
}